import React, { useContext, useEffect, useState } from 'react'

import { TabContainer, TabContent, TabList } from 'components/shared/tabs'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from 'components/ui/accordion'
import { Button } from 'components/ui/button'
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  Dialog as DialogRoot,
  DialogTitle
} from 'components/ui/dialog'
import { XIcon } from 'components/ui/sheet'
import { Switch } from 'components/ui/switch'
import { usePageConfig } from 'context/page-config-context'
import PortalConfigContext from 'context/PortalConfigContext'
import useEcpTranslation from 'hooks/common/useEcpTranslation'
import { getCurrentLanguage } from 'utils/LanguageUtils'
import { cn } from 'utils/StyleUtils'

import {
  tabs as consentTabs,
  generateCookiePreferences,
  googleAnalyticsHeader
} from './consent-config'
import { getFooterLinksFromPage } from './utils'

export const CookieConsent = window?.['CookieConsent']

export const getCookieConsent = () => {
  const raw = CookieConsent?.get('CookieConsent')

  if (!raw) return {}

  try {
    return JSON.parse(raw)
  } catch {
    return {}
  }
}

const clearPreferenceStorageItems = () => {
  localStorage.removeItem('language')
  localStorage.removeItem('theme')
}

type Props = {
  isGoogleAnalyticsEnabled: boolean
}

const defaultCookieValue = {
  necessary: true,
  preferences: false,
  statistics: false
  // marketing: true // TODO: check with team on this
}

const readStoredCookieValue = (): Record<string, boolean> => {
  const stored = getCookieConsent()

  return Object.fromEntries(
    Object.keys(defaultCookieValue).map((key) => [
      key,
      key === 'necessary' ? true : Boolean(stored?.[key])
    ])
  )
}

const Badge: React.FC<React.ComponentPropsWithoutRef<'span'>> = ({
  children,
  className,
  ...props
}) => (
  <span
    className={cn(
      'rounded-md relative top-1 text-gray bg-base-gray-soft size-5 text-xs justify-center items-center text-center inline-flex font-normal',
      className
    )}
    {...props}
  >
    {children}
  </span>
)

const CookieConsentDialog: React.FC<Props> = ({ isGoogleAnalyticsEnabled }) => {
  const { t } = useEcpTranslation()
  const [cookieValue, setCookieValue] = useState<Record<string, boolean>>(() =>
    readStoredCookieValue()
  )
  const [selectedIndex, setSelectedIndex] = useState('consent')
  const { data: portalConfigData } = useContext(PortalConfigContext)
  const {
    data: pageData,
    setIsCookieConsentDialogOpen: setOpen,
    isCookieConsentDialogOpen: open
  } = usePageConfig()

  const preferences = generateCookiePreferences(
    portalConfigData?.portalConfig?.domain ?? ''
  )

  const footerLinks = getFooterLinksFromPage(pageData?.selectedPage)

  const tabs = consentTabs.map((tab) => ({
    ...tab,
    label: t(`cookie.tabs.${tab.id}`)
  }))

  useEffect(() => {
    if (!CookieConsent.hasConsent()) {
      setOpen(true)
    }
  }, [setOpen])

  // Rehydrate the draft from the cookie every time the dialog opens, so
  // external triggers (e.g. footer "Manage cookies") reflect the saved choice.
  useEffect(() => {
    if (open) {
      setCookieValue(readStoredCookieValue())
    }
  }, [open])

  const persistConsent = (value: Record<string, boolean>) => {
    CookieConsent.set(
      'CookieConsent',
      JSON.stringify({
        ...value,
        method: 'explicit',
        utc: Date.now(),
        version: '1'
      }),
      365 // expires in 1 year
    )
  }

  const handleDenyNonEssential = () => {
    persistConsent({ necessary: true, preferences: false, statistics: false })
    clearPreferenceStorageItems()
    setOpen(false)
  }

  const handleAllowSelection = () => {
    persistConsent(cookieValue)
    if (!cookieValue.preferences) {
      clearPreferenceStorageItems()
    }
    setOpen(false)
  }

  const handleAllowAll = () => {
    persistConsent(
      Object.fromEntries(
        Object.keys(defaultCookieValue).map((type) => [type, true])
      )
    )
    setOpen(false)
  }

  const handleTabChange = (index: string) => {
    setSelectedIndex(index)
  }

  const handleCookieChange = (name: string, checked: boolean) => {
    setCookieValue((prevState) => ({ ...prevState, [name]: checked }))
  }

  if (!open) {
    return null
  }

  const links = footerLinks.length > 0 && (
    <>
      <p className="text-sm leading-6">{t('cookie.consent.more_info')}</p>
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap gap-2 text-sm">
          {(() => {
            const items = []

            footerLinks.forEach((link, index) => {
              const currentLanguage = getCurrentLanguage()
              const linkLabel = link.label?.[currentLanguage]
              const linkUrl = link.url?.[currentLanguage]

              if (linkLabel && linkUrl) {
                items.push(
                  <a
                    className="underline focus-visible:ring focus-visible:ring-offset-2 rounded-sm [text-underline-position:under] text-blue-600 hover:text-blue-800"
                    href={linkUrl}
                    key={`footer-link-${index}`}
                    rel="noreferrer"
                    target="_blank"
                  >
                    {linkLabel}
                    <span className="sr-only">
                      {' '}
                      ({t('common.opens_in_new_tab')})
                    </span>
                  </a>
                )

                if (index < footerLinks.length - 1) {
                  items.push(
                    <span aria-hidden="true" key={`footer-sep-${index}`}>
                      &bull;
                    </span>
                  )
                }
              }
            })

            return items
          })()}
        </div>
      </div>
    </>
  )

  return (
    <DialogRoot
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleDenyNonEssential()
      }}
      open={open}
    >
      <DialogContent
        className="flex w-[90%] max-h-[90vh] md:max-w-2xl! sm:w-full flex-col gap-0 p-0 overflow-y-auto"
        data-testid="cookie-consent"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">
          {t('cookie.consent.title')}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {t('cookie.consent.description')}
        </DialogDescription>

        <TabContainer
          className="h-full gap-4 flex flex-col"
          onValueChange={handleTabChange}
          value={selectedIndex}
        >
          <div className="flex items-start justify-between gap-2 px-4 pt-4">
            <TabList tabs={tabs} />
            <DialogClose asChild>
              <Button
                aria-label={t('common.close')}
                className="text-spark-a10 -mt-1 -mr-1"
                variant="icon"
              >
                <XIcon />
              </Button>
            </DialogClose>
          </div>

          <div className="flex flex-col overflow-y-auto max-h-96 px-6">
            <TabContent
              className="gap-4 h-full"
              key={`tab-content-${selectedIndex}`}
              value={selectedIndex}
            >
              <div className="flex flex-col gap-4">
                {selectedIndex === 'consent' && (
                  <div className="flex flex-col gap-2.5">
                    <span aria-hidden className="text-2xl font-bold m-0">
                      {t('cookie.consent.title')}
                    </span>
                    <p aria-hidden className="text-sm leading-6">
                      {t('cookie.consent.description')}
                    </p>

                    {links}
                  </div>
                )}
                {selectedIndex === 'details' && (
                  <div className="flex flex-col gap-0">
                    {preferences.map((preference, index) => {
                      const filterdPreferences = preference.accordion.filter(
                        (item) => {
                          // Only show "Google Analytics" in cookie consent if it is enabled in 360 portal
                          if (item.header === googleAnalyticsHeader) {
                            return isGoogleAnalyticsEnabled
                          }

                          return true
                        }
                      )

                      const entriesCount = filterdPreferences.reduce(
                        (acc, item) => acc + item.details.length,
                        0
                      )
                      const labelId = `cookie-details-${preference.id}-label`
                      const descriptionId = `cookie-details-${preference.id}-description`
                      const countId = `cookie-details-${preference.id}-count`

                      return (
                        <section
                          aria-describedby={descriptionId}
                          aria-labelledby={labelId}
                          className={cn('border-gray border-b', {
                            'border-0': index === preferences.length - 1
                          })}
                          key={preference.id}
                        >
                          <div className="flex justify-between w-full pt-3 pb-3">
                            <div className="flex gap-2 justify-between">
                              <span className="font-bold text-lg" id={labelId}>
                                {t(`cookie.details.${preference.id}.title`)}
                              </span>
                              <Badge id={countId}>
                                <span aria-hidden>{entriesCount}</span>
                                <span className="sr-only">
                                  {t('cookie.details.entries_count', {
                                    count: entriesCount
                                  })}
                                </span>
                              </Badge>
                            </div>
                            {preference.id !== 'unclassified' &&
                              preference.id !== 'necessary' && (
                                <Switch
                                  aria-describedby={countId}
                                  aria-labelledby={labelId}
                                  checked={cookieValue[preference.id]}
                                  onCheckedChange={(checked) => {
                                    handleCookieChange(preference.id, checked)
                                  }}
                                  value={t(
                                    `cookie.${
                                      cookieValue[preference.id]
                                        ? 'switch_on'
                                        : 'switch_off'
                                    }`
                                  )}
                                />
                              )}
                          </div>
                          <p className="text-sm pb-2 pt-0" id={descriptionId}>
                            {t(`cookie.details.${preference.id}.description`)}
                          </p>

                          {preference.accordion.length > 0 && (
                            <Accordion
                              className="last:mb-4 flex flex-col"
                              type="multiple"
                            >
                              {filterdPreferences.map((item, itemIndex) => (
                                <AccordionItem
                                  className="border-0"
                                  key={`${item.header}-${itemIndex}`}
                                  value={`${item.header}-${itemIndex}`}
                                >
                                  <AccordionTrigger className="text-sm tracking-wide font-semibold cursor-pointer py-1 break-all text-left">
                                    {item.header}
                                  </AccordionTrigger>
                                  <AccordionContent className="flex flex-col">
                                    {item?.details.map(
                                      (detail, detailIndex) => (
                                        <div
                                          className={cn(
                                            'gap-2 flex px-2 flex-col border-b py-3 border-gray',
                                            {
                                              'border-0':
                                                detailIndex ===
                                                item.details.length - 1
                                            }
                                          )}
                                          key={detail.title}
                                        >
                                          <div className="flex justify-between">
                                            <h4 className="font-bold text-xs">
                                              {t(detail.title)}
                                            </h4>
                                          </div>
                                          <p className="text-xs text-gray-light">
                                            {t(detail.description)}
                                          </p>
                                          <div className="flex gap-1 text-xs text-gray-light">
                                            <p>
                                              <span className="font-semibold text-gray">
                                                {t('cookie.expiry')}:{' '}
                                              </span>
                                              <span className="text-gray-light">
                                                {t(detail.expiry)},
                                              </span>
                                            </p>
                                            <p>
                                              <span className="font-semibold text-gray">
                                                {t('cookie.type')}:
                                              </span>{' '}
                                              <span className="text-gray-light">
                                                {t(detail.type)}
                                              </span>
                                            </p>
                                          </div>
                                        </div>
                                      )
                                    )}
                                  </AccordionContent>
                                </AccordionItem>
                              ))}
                            </Accordion>
                          )}
                        </section>
                      )
                    })}
                  </div>
                )}

                {selectedIndex === 'about' && (
                  <div className="flex flex-col gap-4 pt-2">
                    <p className="text-sm leading-6">
                      {t('cookie.about.description')}
                    </p>

                    {links}
                  </div>
                )}
              </div>
            </TabContent>
          </div>
        </TabContainer>

        <DialogFooter className="flex w-full p-6 shrink-0 gap-4 md:gap-2 items-center justify-center flex-col overflow-hidden bg-slate-2 md:justify-end md:flex-row">
          <Button
            className="w-full md:w-fit max-sm:min-w-40"
            onClick={handleDenyNonEssential}
            variant="secondary"
          >
            {t('cookie.deny_non_essential')}
          </Button>
          <Button
            className="w-full md:w-fit max-sm:min-w-40"
            onClick={handleAllowSelection}
            variant="secondary"
          >
            {t('cookie.allow_selection')}
          </Button>
          <Button
            className="w-full md:w-fit"
            onClick={handleAllowAll}
            variant="secondary"
          >
            {t('cookie.allow_all')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}

export default CookieConsentDialog
